import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Receipt,
  Download,
  Info,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  listInvoices,
  downloadInvoicePdf,
  planLabel,
} from "../../actions/billing";

// GET /billing/invoices returns one row per completed charge. The field
// names couldn't be confirmed against a live session — the org has no paid
// history yet — so each is read through a few likely spellings, and anything
// missing renders as a dash rather than breaking the row.

const firstOf = (source, keys) => {
  if (!source) return undefined;

  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }

  return undefined;
};

const money = (amount, currency) => {
  const value = Number(amount);
  if (!Number.isFinite(value)) return "—";

  // The API reports major units, and some gateways report cents; a value with
  // no decimal part and a currency that has them is still ambiguous, so this
  // trusts the number as given rather than guessing a division by 100.
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
    }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency || ""}`.trim();
  }
};

const when = (value) => {
  if (!value) return "—";

  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
};

const readInvoice = (raw, index) => ({
  id: firstOf(raw, ["id", "invoiceId", "_id"]) || `invoice-${index}`,
  reference:
    firstOf(raw, [
      "invoiceNumber",
      "number",
      "transactionId",
      "helcimTransactionId",
      "paypalTransactionId",
      "paypalCaptureId",
      "id",
    ]) || "—",
  date: when(
    firstOf(raw, ["paidAt", "createdAt", "date", "time", "completedAt"]),
  ),
  amount: money(
    firstOf(raw, ["amount", "total", "value", "grossAmount"]),
    firstOf(raw, ["currency", "currencyCode"]),
  ),
  plan: firstOf(raw, ["plan", "planName", "tier"]),
  status: firstOf(raw, ["status", "state"]) || "Paid",
});

// Decorative only, and built the same way as the payment-methods hero: a
// document over a soft blob rather than a raster asset.
const InvoiceArt = () => (
  <svg
    className="bill-pm-art"
    viewBox="0 0 200 130"
    aria-hidden="true"
    focusable="false"
  >
    <defs>
      <linearGradient id="bill-inv-doc" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#8b8ff5" />
        <stop offset="100%" stopColor="#6366f1" />
      </linearGradient>
      <linearGradient id="bill-inv-front" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#4f46e5" />
        <stop offset="100%" stopColor="#3f44e8" />
      </linearGradient>
    </defs>

    <rect x="18" y="20" width="84" height="100" rx="10" fill="url(#bill-inv-doc)" opacity="0.85" />

    <rect x="62" y="10" width="92" height="108" rx="11" fill="url(#bill-inv-front)" />
    <rect x="76" y="28" width="50" height="7" rx="3.5" fill="#ffffff" opacity="0.5" />
    <rect x="76" y="46" width="64" height="6" rx="3" fill="#ffffff" opacity="0.3" />
    <rect x="76" y="60" width="54" height="6" rx="3" fill="#ffffff" opacity="0.3" />
    <rect x="76" y="74" width="40" height="6" rx="3" fill="#ffffff" opacity="0.3" />
    <rect x="76" y="92" width="30" height="9" rx="4.5" fill="#ffffff" opacity="0.6" />

    <circle cx="160" cy="92" r="24" fill="#a5b4fc" />
    <path
      d="M150 92l7 7 14-15"
      fill="none"
      stroke="#ffffff"
      strokeWidth="5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Matches the page size the endpoint defaults to.
const PER_PAGE = 10;

export default function InvoicesPanel() {
  const dispatch = useDispatch();

  const [invoices, setInvoices] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // The row whose PDF is being fetched, so only its button shows as busy.
  const [downloading, setDownloading] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    dispatch(listInvoices({ page, limit: PER_PAGE })).then((result) => {
      if (cancelled) return;

      setLoading(false);

      if (!result?.success) {
        setError(result?.message || "We couldn't load your payment history.");
        return;
      }

      setError("");
      setInvoices((result.invoices || []).map(readInvoice));
      setPagination(result.pagination || null);
    });

    return () => {
      cancelled = true;
    };
  }, [dispatch, page]);

  const handleDownload = async (id) => {
    setDownloading(id);
    setError("");

    const result = await dispatch(downloadInvoicePdf(id));

    setDownloading("");

    if (!result?.success) {
      setError(result?.message || "We couldn't download that invoice.");
    }
  };

  const currentPage = pagination?.page || page;
  const limit = pagination?.limit || PER_PAGE;
  const total = pagination?.total ?? invoices.length;
  const totalPages = pagination?.totalPages || 1;

  // Offsets come from the page size, not the row count: the last page is
  // short and deriving the range from it would misreport it.
  const firstRow = total === 0 ? 0 : (currentPage - 1) * limit + 1;
  const lastRow = Math.min(firstRow + invoices.length - 1, total);

  // A window around the current page; every page as a button stops being
  // usable once an account has years of charges.
  const windowStart = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
  const pageNumbers = Array.from(
    { length: Math.min(5, totalPages) },
    (_, i) => windowStart + i,
  ).filter((number) => number >= 1 && number <= totalPages);

  const renderBody = () => {
    if (loading) {
      return (
        <div className="bill-inv-rows" aria-busy="true">
          {[0, 1, 2].map((row) => (
            <div className="bill-inv-row" key={row}>
              <span className="skel acct-skel--label" />
              <span className="skel acct-skel--desc" />
              <span className="skel acct-skel--label" />
              <span className="skel acct-skel--desc" />
            </div>
          ))}
        </div>
      );
    }

    if (error) return <p className="auth-error">{error}</p>;

    if (!invoices.length) {
      return (
        <div className="bill-inv-empty">
          <span className="bill-inv-empty__icon">
            <Receipt size={26} strokeWidth={1.7} />
          </span>
          <strong>No payments yet</strong>
          <p>
            Charges appear here once a paid plan renews. Free plans are never
            billed, so there is nothing to show.
          </p>
        </div>
      );
    }

    return (
      <div className="bill-inv-table-wrap">
        <table className="bill-inv-table">
          <thead>
            <tr>
              <th className="text-center">Date</th>
              <th className="text-center">Reference</th>
              <th className="text-center">Plan</th>
              <th className="text-center">Amount</th>
              <th className="text-center">Status</th>
              <th className="text-center" aria-label="Invoice" />
            </tr>
          </thead>
          <tbody>
            {invoices.map((invoice) => (
              <tr key={invoice.id}>
                <td className="text-center">{invoice.date}</td>
                <td className="bill-inv-ref text-center">{invoice.reference}</td>
                <td className="text-center">
                  {invoice.plan ? planLabel(invoice.plan) : "—"}
                </td>
                <td className="bill-inv-amount text-center">{invoice.amount}</td>
                <td className="text-center">
                  <span
                    className={`bill-pm-status${
                      /paid|complete|success/i.test(invoice.status)
                        ? " is-active"
                        : " is-pending"
                    }`}
                  >
                    {planLabel(invoice.status)}
                  </span>
                </td>
                <td className="text-center">
                  {/* A button, not a link: the PDF sits behind the session
                      token, so it has to be fetched rather than navigated to. */}
                  <button
                    type="button"
                    className="bill-inv-receipt"
                    onClick={() => handleDownload(invoice.id)}
                    disabled={downloading === invoice.id}
                  >
                    {downloading === invoice.id ? (
                      <span className="install-status-spinner" aria-hidden="true" />
                    ) : (
                      <Download size={15} strokeWidth={2} />
                    )}
                    {downloading === invoice.id ? "Preparing…" : "Invoice"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="bill-pm">
      <header className="bill-pm-hero">
        <div className="bill-pm-hero__text">
          <h2 className="bill-pm-hero__title">Invoices</h2>
          <p className="bill-pm-hero__sub">
            Every completed charge for this workspace, most recent first.
            Download any invoice as a PDF for your records.
          </p>
        </div>
        <InvoiceArt />
      </header>
      <section className="dash-card bill-pm-card">
        <div className="bill-pm-card__head">
          <div>
            <h3 className="bill-pm-card__title">Payment history</h3>
            <p className="bill-pm-card__sub">
              {total > 0
                ? `${total} completed ${total === 1 ? "charge" : "charges"} on this workspace.`
                : "Charges appear here once a paid plan renews."}
            </p>
          </div>
        </div>

        {renderBody()}

        {/* Only once there is more than one page: a single page of charges
            needs no controls. */}
        {!loading && !error && totalPages > 1 && (
          <div className="bill-inv-pager">
            <span>
              Showing {firstRow} to {lastRow} of {total} payments
            </span>

            <div className="bill-inv-pager__controls">
              <button
                type="button"
                onClick={() => setPage((value) => Math.max(1, value - 1))}
                disabled={currentPage <= 1}
                aria-label="Previous page"
              >
                <ChevronLeft size={16} strokeWidth={2} />
              </button>

              {pageNumbers.map((number) => (
                <button
                  type="button"
                  key={number}
                  className={number === currentPage ? "is-active" : ""}
                  onClick={() => setPage(number)}
                >
                  {number}
                </button>
              ))}

              <button
                type="button"
                onClick={() =>
                  setPage((value) => Math.min(totalPages, value + 1))
                }
                disabled={currentPage >= totalPages}
                aria-label="Next page"
              >
                <ChevronRight size={16} strokeWidth={2} />
              </button>
            </div>
          </div>
        )}

        <p className="bill-pm-note">
          <span className="bill-pm-note__icon">
            <Info size={17} strokeWidth={2} />
          </span>
          Invoices are issued by Hostor and charged to your card. Contact
          support if a charge looks wrong.
        </p>
      </section>
    </div>
  );
}
